import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { JourneyBanner } from "@fox1ck-org/auth/journey/banner";
import { useBrandAnswer } from "@fox1ck-org/auth/saas/react";
import { useShellIdentity } from "@fox1ck-org/auth/ui";
import {
  AppShell,
  CommandPalette,
  useCommandPalette,
  type AppShellLinkProps,
  type CommandAction,
} from "@fox1ck-org/ui";
import {
  Building2,
  FolderTree,
  GitPullRequest,
  Grid3X3,
  HardDrive,
  KeyRound,
  Layers,
  LayoutDashboard,
  Network,
  Route as RouteIcon,
  ScrollText,
  Server,
  Terminal,
  Users,
  type LucideIcon,
} from "lucide-react";

import { api } from "@/api/client";
import { useIsPlatformAdmin } from "@/lib/auth";

interface Entry {
  id: string;
  label: string;
  href: string;
  icon: LucideIcon;
  adminOnly?: boolean;
}

interface Section {
  id: string;
  label: string;
  items: Entry[];
}

const SECTIONS: Section[] = [
  {
    id: "infrastructure",
    label: "Инфраструктура",
    items: [
      { id: "dashboard", label: "Обзор", href: "/", icon: LayoutDashboard },
      { id: "logs", label: "Журналы", href: "/logs", icon: Terminal },
      { id: "backups", label: "Резервные копии", href: "/backups", icon: HardDrive },
    ],
  },
  {
    id: "services",
    label: "Сервисы",
    items: [{ id: "environments", label: "Окружения", href: "/environments", icon: Layers }],
  },
  {
    id: "access",
    label: "Доступ",
    items: [
      { id: "org-structure", label: "Оргструктура", href: "/org-structure", icon: Network, adminOnly: true },
      { id: "access-matrix", label: "Матрица доступа", href: "/access-matrix", icon: Grid3X3, adminOnly: true },
      { id: "app-roles", label: "Роли приложений", href: "/app-roles", icon: KeyRound, adminOnly: true },
      { id: "users", label: "Пользователи", href: "/users", icon: Users, adminOnly: true },
      { id: "groups", label: "Группы", href: "/groups", icon: FolderTree, adminOnly: true },
      { id: "organizations", label: "Организации", href: "/organizations", icon: Building2 },
    ],
  },
  {
    id: "administration",
    label: "Администрирование",
    items: [
      { id: "gateway-routes", label: "Маршруты шлюза", href: "/gateway-routes", icon: RouteIcon, adminOnly: true },
      { id: "gitops", label: "Запросы GitOps", href: "/gitops", icon: GitPullRequest, adminOnly: true },
    ],
  },
];

const PHONE_NAV = ["dashboard", "logs", "backups", "environments", "organizations"];

const renderLink = ({ href, ...props }: AppShellLinkProps) => <Link to={href} {...props} />;

const toItem = ({ id, label, href, icon: Icon }: Entry) => ({ id, label, href, icon: <Icon strokeWidth={1.5} /> });

/**
 * The frame is the kit's (board K3Shell): the organisation and the service on
 * the rail, the person at its foot, search in the topbar. The admin screens
 * appear for the client role only.
 */
export function Shell({ children }: { children: ReactNode }) {
  const shell = useShellIdentity();
  const brand = useBrandAnswer();
  const isPlatformAdmin = useIsPlatformAdmin();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const palette = useCommandPalette();
  const [search, setSearch] = useState("");

  const sections = useMemo(
    () =>
      SECTIONS.map((section) => ({
        ...section,
        items: section.items.filter((entry) => isPlatformAdmin || !entry.adminOnly),
      })).filter((section) => section.items.length > 0),
    [isPlatformAdmin],
  );
  const entries = sections.flatMap((section) => section.items);
  const active = entries.find((entry) =>
    entry.href === "/" ? pathname === "/" : pathname === entry.href || pathname.startsWith(`${entry.href}/`),
  );

  useEffect(() => {
    document.title = brand.documentTitle(active?.label);
  }, [brand, active?.label]);

  const { data: appsData } = useQuery({
    queryKey: ["cluster-apps", "prod"],
    queryFn: () => api.cluster.listApps("prod"),
    enabled: palette.open,
    staleTime: 60_000,
  });

  const actions: CommandAction[] = [
    ...entries.map(toItem).map((item) => ({
      id: item.id,
      label: item.label,
      group: "Разделы",
      icon: item.icon,
      onSelect: () => navigate(item.href),
    })),
    ...(appsData?.apps ?? []).flatMap((app) => [
      {
        id: `app-${app.name}`,
        label: app.name,
        group: "Приложения",
        icon: <Server strokeWidth={1.5} />,
        detail: app.namespace,
        onSelect: () => navigate(`/apps/${app.name}`),
      },
      {
        id: `logs-${app.name}`,
        label: `Журнал ${app.name}`,
        group: "Журналы приложений",
        icon: <ScrollText strokeWidth={1.5} />,
        detail: "в реальном времени",
        onSelect: () => navigate(`/apps/${app.name}?tab=logs&mode=live`),
      },
    ]),
  ];

  return (
    <>
      <AppShell
        brand={shell.brand}
        account={shell.account}
        banner={<JourneyBanner app="app-manager" />}
        sections={sections.map((section) => ({ ...section, items: section.items.map(toItem) }))}
        activeId={active?.id}
        renderLink={renderLink}
        locale="ru-RU"
        breadcrumb={<span className="text-[13px] font-medium text-foreground">{active?.label}</span>}
        search={{ placeholder: "Разделы и приложения", onOpen: () => palette.setOpen(true) }}
        mobileNav={entries.filter((entry) => PHONE_NAV.includes(entry.id)).map(toItem)}
        labels={{
          nav: "Разделы",
          collapse: "Свернуть меню",
          expand: "Развернуть меню",
          shortcutGroup: "Меню",
          mobileNav: "Разделы",
        }}
      >
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">{children}</div>
      </AppShell>
      <CommandPalette
        open={palette.open}
        onOpenChange={palette.setOpen}
        actions={actions}
        search={search}
        onSearchChange={setSearch}
        labels={{ title: "Поиск", placeholder: "Куда перейти…", empty: "Ничего не нашлось" }}
      />
    </>
  );
}
